import { Chip } from '@material-ui/core';
import { TableColumn } from '@backstage/core-components';
import { CatalogTableRow, CatalogTable } from '@backstage/plugin-catalog';
import {
  getLocalDev,
  localDevColor,
  localDevLabel,
  localDevValueLabel,
  type LocalDevField,
} from './localDev';
import { getAwsAccountId, getAwsAccountEnvironment } from './awsAccount';

function localDevColumn(field: LocalDevField): TableColumn<CatalogTableRow> {
  return {
    title: localDevLabel(field),
    // Hidden by default; users can enable it via the table's columns button.
    hidden: true,
    hiddenByColumnsButton: false,
    customSort: (a, b) =>
      (getLocalDev(a.entity, field) ?? '').localeCompare(
        getLocalDev(b.entity, field) ?? '',
      ),
    customFilterAndSearch: (query, row) => {
      const value = getLocalDev(row.entity, field);
      return value
        ? localDevValueLabel(field, value)
            .toLowerCase()
            .includes(query.toLowerCase())
        : false;
    },
    render: ({ entity }) => {
      const value = getLocalDev(entity, field);
      if (!value) return null;
      return (
        <Chip
          label={localDevValueLabel(field, value)}
          size="small"
          style={{
            backgroundColor: localDevColor(field, value),
            color: '#fff',
          }}
        />
      );
    },
  };
}

/**
 * AWS account id, read from the `amazonaws.com/account-id` annotation on
 * aws-account Resource entities. Hidden by default; enable via the columns
 * button. Empty for entities without the annotation.
 */
const awsAccountColumn: TableColumn<CatalogTableRow> = {
  title: 'AWS Account',
  hidden: true,
  hiddenByColumnsButton: false,
  customSort: (a, b) =>
    (getAwsAccountId(a.entity) ?? '').localeCompare(
      getAwsAccountId(b.entity) ?? '',
    ),
  customFilterAndSearch: (query, row) => {
    const id = getAwsAccountId(row.entity);
    const env = getAwsAccountEnvironment(row.entity);
    return [id, env]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(query.toLowerCase());
  },
  render: ({ entity }) => {
    const id = getAwsAccountId(entity);
    if (!id) return null;
    const env = getAwsAccountEnvironment(entity);
    return <Chip label={env ? `${id} (${env})` : id} size="small" />;
  },
};

export const catalogColumns: TableColumn<CatalogTableRow>[] = [
  CatalogTable.columns.createTitleColumn({ hidden: true }),
  CatalogTable.columns.createNameColumn({ defaultKind: 'Component' }),
  CatalogTable.columns.createOwnerColumn(),
  CatalogTable.columns.createSpecTypeColumn({ hidden: false }),
  CatalogTable.columns.createSpecLifecycleColumn(),
  localDevColumn('docs'),
  localDevColumn('status'),
  awsAccountColumn,
  CatalogTable.columns.createTagsColumn(),
];
